import clsx from "clsx";
// Helpers
import { isNumeric } from "@/Common/helpers/Verifications/numbers";
import { computeProps, isEmptyRow } from "@/Common/helpers/Tables";
import { ClassNameHints } from "@/Common/types/tables";
import { applyFormatter } from "@/Washouts/helpers/tables";
import generateTailwindVariableClasses from "@/Common/helpers/TailwindCSS";
import { and, nullishResolver, or, ternaryResolver } from "@/Common/helpers/sonar";

// MUI
import { SxProps, Tooltip, TooltipProps, Typography } from "@mui/material";

// Types & Constants
import { GridColDef } from "@mui/x-data-grid-premium";
import { TypographyCellProps } from "../types";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

export const OFFSET_SKIDDING = 0;
export const OFFSET_DISTANCE = -4;

function getTextToShow({ params, ...props }: TypographyCellProps): string {
	let txt;
	switch (props.render) {
		case "absolute":
			txt = applyFormatter(params, "absolute", props?.formatOptions);
			break;
		case "account":
			txt = applyFormatter(params, "account", props?.formatOptions);
			break;
		default:
			txt = params?.formattedValue ?? params?.value;
	}
	const isNumber = params?.colDef.type === "number" || isNumeric(txt) || ["absolute", "account"].includes(props.render ?? "");

	if (and(!txt, isNumber) || and(!txt, Boolean(props.dashed))) {
		txt = "-";
	}
	return txt;
}

function getTooltipPlacement(rowAlign: GridColDef["align"], rowIndex: number): TooltipProps["placement"] {
	if (rowIndex > 1) {
		switch (rowAlign) {
			case "right":
				return "top-end";
			case "left":
				return "top-start";
			default:
				return "top";
		}
	}
	switch (rowAlign) {
		case "right":
			return "bottom-end";
		case "left":
			return "bottom-start";
		default:
			return "bottom";
	}
}

function isAggregation(hints: ClassNameHints): boolean {
	return hints?.isFooter || Boolean(hints?.isSubTotalRow);
}

function isNormalRow(hints: ClassNameHints): boolean {
	return !isAggregation(hints) && !hints?.isNegative;
}

function hasBefore(hints: ClassNameHints): boolean {
	return hints?.isMoney || hints?.isDollarPerUnit || Boolean(hints?.isPvr);
}

export function TooltipTitleWrapper({ text }: Readonly<{ text: string }>) {
	return (
		<p
			className={clsx({
				"!font-['PTSansPro'] !text-[11px]": true,
				"[&_*]:!font-['PTSansPro'] [&_*]:!text-[11px]": true,
			})}
		>
			{text}
		</p>
	);
}

const isCellDashed = ({ dashed = false, render = "", isMoney = false, isDollar = false, isDollarPerUnit = false }) =>
	nullishResolver(dashed, render === "account" || isMoney || isDollar || isDollarPerUnit);

const getCustomSx = ({ customAfter = "", isEmpty = false, finalHints = {} }): SxProps => {
	const { isPercentage = false, isUnit = false } = (finalHints ?? {}) as ClassNameHints;
	const isAfterContent = isPercentage || isUnit;

	if (and(Boolean(customAfter), isAfterContent, !isEmpty)) {
		return { "&:after": { content: `'${customAfter}'` } };
	}

	return {};
};

export function TypographyCell({
	render = "default",
	reference,
	params,
	issues,
	hints,
	dashed,
	tooltipProps,
	hasTooltip = true,
	forceTooltip = false,
	fontSize = "12px",
	fontLineHeight = "19px",
	fontWeight = "500",
	customAfter = "",
	enableOverrideMode = false,
	...props
}: Readonly<TypographyCellProps>) {
	const ref = useRef<HTMLParagraphElement>(null);
	const isEmpty = nullishResolver(isEmptyRow(params), false);
	const finalHints = { ...computeProps(reference, params, issues), ...hints };
	const isDashed = isCellDashed({ ...finalHints, dashed, render });
	const txt = ternaryResolver(isEmpty, "", getTextToShow({ render, params, dashed: isDashed, ...props }));
	const [isOverflown, setIsOverflown] = useState(false);
	const innerContent = useMemo(() => nullishResolver(props.children, txt), [props.children, txt]);

	useLayoutEffect(() => {
		const elem = ref.current;
		if (elem) {
			const hasOverflow = elem.scrollWidth > elem.clientWidth || elem.scrollHeight > elem.clientHeight;
			setIsOverflown(hasOverflow);
		}
	}, [ref, ref?.current?.scrollWidth, ref?.current?.clientWidth, ref?.current?.scrollHeight, ref?.current?.clientHeight]);

	const {
		isNegative = false,
		isFooter = false,
		isDollarPerUnit = false,
		isHours = false,
		isMoney = false,
		isPercentage = false,
		isUnit = false,
		isPvr = false,
		isSubTotalRow = false,
		noWrap = false,
	} = finalHints ?? {};

	const cellContent = useMemo(
		() => (
			<Typography
				ref={ref}
				{...props}
				className={
					and(Boolean(props.className), enableOverrideMode)
						? props.className
						: clsx({
								[`${nullishResolver(props.className, "")}`]: true,
								"pt-[3px] truncate": true,
								"!font-[--font-weight-param]": or(isNormalRow(finalHints), and(isAggregation(finalHints), !isNegative)),
								"!text-[length:--font-size-param] !leading-[--font-line-height-param]": true,
								"!text-[color:--text-color]": isNormalRow(finalHints),
								"!font-[600]": and(isAggregation(finalHints), isNegative),
								"!text-white": and(isAggregation(finalHints), !isNegative),
								"!font-sans !before:font-sans !after:font-sans": !isAggregation(finalHints),
								"!font-montserrat !before:font-montserrat !after:font-montserrat": isAggregation(finalHints),
								"!overflow-visible": and(isFooter, noWrap),
								"text-[color:--text-alert-color] before:text-[color:--text-alert-color] after:text-[color:--text-alert-color]":
									isNegative,
								"relative before:absolute before:left-0 after:absolute": hasBefore(finalHints),
								"before:content-['$']": and(isMoney, !isEmpty),
								"before:content-['$/u']": and(isDollarPerUnit, !isEmpty),
								"before:content-['PVR:_$']": and(Boolean(isPvr), !isEmpty),
								"!bg-[color:--primary-color] !sticky": and(!isNegative, isFooter),
								"!bg-[color:--neutral-gray]": and(!isNegative, Boolean(isSubTotalRow)),
								"!bg-[color:--light-gray-color-v2] !text-[color:--text-alert-color]": and(
									isNegative,
									isAggregation(finalHints)
								),
								"after:ml-[4px] after:mr-[0px]": and(or(isUnit, isPercentage, isHours, Boolean(customAfter)), !isEmpty),
								"after:content-['%']": and(isPercentage, !isEmpty, !customAfter),
								"after:content-['unit(s)']": and(isUnit, !isEmpty, !customAfter),
								"after:content-['hr(s)']": and(isHours, !isEmpty, !customAfter),
						  })
				}
				style={
					and(Boolean(props.style), enableOverrideMode)
						? props.style
						: {
								...generateTailwindVariableClasses("--font-size-param", fontSize),
								...generateTailwindVariableClasses("--font-line-height-param", fontLineHeight),
								...generateTailwindVariableClasses("--font-weight-param", fontWeight),
						  }
				}
				sx={and(Boolean(props.sx), enableOverrideMode) ? props.sx : getCustomSx({ customAfter, isEmpty, finalHints })}
			>
				{innerContent}
			</Typography>
		),
		[props]
	);

	const { contentAsTitle = true, title = undefined, autoPlacement = true, arrow = true, ...rest } = tooltipProps ?? {};
	if (and(autoPlacement, Boolean(params))) {
		const rowIndex = params?.api.getRowIndexRelativeToVisibleRows(params.id) ?? 0;
		const rowAlign = params?.colDef.align ?? "left";
		rest.placement = getTooltipPlacement(rowAlign, rowIndex);
	} else if (and(autoPlacement, !params)) {
		rest.placement = "top";
	}
	const offset = [OFFSET_SKIDDING, OFFSET_DISTANCE];
	rest.slotProps = { popper: { modifiers: [{ name: "offset", options: { offset } }] } };
	const wrappedTitle = typeof title === "string" ? <TooltipTitleWrapper text={title} /> : title;
	const finalTitle = wrappedTitle ?? (contentAsTitle ? <TooltipTitleWrapper text={txt} /> : null);

	if (or(isEmpty, !hasTooltip)) {
		return <>{cellContent}</>;
	}
	return (
		<Tooltip title={or(isOverflown, forceTooltip) ? finalTitle : null} arrow={arrow} {...rest}>
			{cellContent}
		</Tooltip>
	);
}

export default TypographyCell;
